/**
 * Approximate IANA timezone from GPS coordinates.
 *
 * Uses a set of known US timezone boundary longitudes with latitude
 * adjustments for irregular borders, then falls back to a global
 * longitude-based estimate. Accurate enough for race-track locations
 * in the continental US.
 */

const US_ZONES = [
  // [minLng, maxLng, iana, optional lat constraint]
  { minLng: -67.5, maxLng: -55, tz: 'America/Puerto_Rico' },      // Atlantic
  { minLng: -80.5, maxLng: -67.5, tz: 'America/New_York' },       // Eastern
  { minLng: -87.5, maxLng: -80.5, tz: 'America/New_York', maxLat: 35 }, // FL panhandle / GA
  { minLng: -87.5, maxLng: -80.5, tz: 'America/Chicago' },        // Central (upper)
  { minLng: -100,  maxLng: -87.5, tz: 'America/Chicago' },        // Central
  { minLng: -105,  maxLng: -100,  tz: 'America/Denver' },         // Mountain (east)
  { minLng: -115,  maxLng: -105,  tz: 'America/Denver' },         // Mountain (west)
  { minLng: -125,  maxLng: -115,  tz: 'America/Los_Angeles' },    // Pacific
  { minLng: -165,  maxLng: -125,  tz: 'America/Anchorage' },      // Alaska
  { minLng: -180,  maxLng: -165,  tz: 'Pacific/Honolulu' },       // Hawaii-ish
];

// Global fallback: offset hours -> representative IANA timezone
const GLOBAL_OFFSETS = [
  [-12, 'Etc/GMT+12'], [-11, 'Pacific/Pago_Pago'], [-10, 'Pacific/Honolulu'],
  [-9, 'America/Anchorage'], [-8, 'America/Los_Angeles'], [-7, 'America/Denver'],
  [-6, 'America/Chicago'], [-5, 'America/New_York'], [-4, 'America/Halifax'],
  [-3, 'America/Sao_Paulo'], [-2, 'Atlantic/South_Georgia'], [-1, 'Atlantic/Azores'],
  [0, 'Europe/London'], [1, 'Europe/Paris'], [2, 'Europe/Helsinki'],
  [3, 'Europe/Moscow'], [4, 'Asia/Dubai'], [5, 'Asia/Karachi'],
  [6, 'Asia/Dhaka'], [7, 'Asia/Bangkok'], [8, 'Asia/Shanghai'],
  [9, 'Asia/Tokyo'], [10, 'Australia/Sydney'], [11, 'Pacific/Noumea'],
  [12, 'Pacific/Auckland'],
];

export function getTimezone(lat, lng) {
  if (lat == null || lng == null) return null;

  // Try US zones first (most tracks are in the US)
  for (const zone of US_ZONES) {
    if (lng >= zone.minLng && lng < zone.maxLng) {
      if (zone.maxLat != null && lat > zone.maxLat) continue;
      return zone.tz;
    }
  }

  // Global fallback from longitude
  const offsetHours = Math.round(lng / 15);
  const entry = GLOBAL_OFFSETS.find(([h]) => h === offsetHours);
  return entry ? entry[1] : 'UTC';
}

/**
 * Format a UTC ISO datetime string to "h:mm AM/PM" in the track's local timezone.
 */
export function formatTimeAtTrack(isoString, timezone) {
  if (!isoString || !timezone) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}
